import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { storage } from '../../utils/storage';
import { db } from '../../config/firebase';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  onSnapshot, 
  doc, 
  updateDoc, 
  getDoc, 
  deleteDoc, 
  getDocs, 
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';

interface Endereco {
  id: string;
  rua: string;
  numero: string;
  adicional: string;
  setor: string;
  principal: boolean;
}

interface Cliente {
  id: string;
  nome: string;
  telefone: string;
  enderecos: Endereco[];
  empresaId?: string;
  endereco?: {
    rua: string;
    numero: string;
    adicional?: string;
    setor?: string;
  };
  setor?: string;
}

interface Setor {
  id?: string;
  nome: string;
  valor: number;
}

interface TabelaTaxas {
  setores: Setor[];
}

const ListaClientes: React.FC = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [clienteEditando, setClienteEditando] = useState<Cliente | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [setores, setSetores] = useState<Setor[]>([]);
  const [pesquisaSetor, setPesquisaSetor] = useState<{[key: string]: string}>({});

  const criarEnderecoPadrao = (): Endereco => ({
    id: Date.now().toString(),
    rua: '',
    numero: '',
    adicional: '',
    setor: '',
    principal: true
  });

  const [novoCliente, setNovoCliente] = useState<{
    nome: string;
    telefone: string;
    enderecos: Endereco[];
  }>({
    nome: '',
    telefone: '',
    enderecos: [criarEnderecoPadrao()]
  });

  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const currentUserId = await storage.getItem('userId');
        if (!currentUserId) {
          navigate('/login');
          return;
        }
        setUserId(currentUserId);
        
        // Buscar dados da empresa para obter a tabela de taxas
        const userDoc = await getDoc(doc(db, 'usuarios', currentUserId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.empresaId) {
            const empresaDoc = await getDoc(doc(db, 'Empresa', userData.empresaId));
            if (empresaDoc.exists()) {
              const empresaData = empresaDoc.data();
              if (empresaData.tabelaTaxaId) {
                await carregarSetores(empresaData.tabelaTaxaId);
              }
            }
            
            // Migrar clientes antigos se necessário
            await migrarClientesAntigos(currentUserId);
            
            // Carregar clientes da subcoleção do usuário
            carregarClientes(currentUserId);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar ID do usuário:', error);
        alert('Não foi possível carregar os dados do usuário.');
      }
    };

    fetchUserId();
  }, [navigate]);

  const carregarClientes = (userId: string) => {
    const q = query(collection(db, 'usuarios', userId, 'clientes'));
    
    return onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => {
        const clienteData = doc.data();
        
        // Migração de dados antigos para o novo formato
        if (!clienteData.enderecos) {
          const enderecoAntigo = clienteData.endereco || {};
          const setorAntigo = clienteData.setor || '';
          
          return {
            id: doc.id,
            ...clienteData,
            enderecos: [{
              id: Date.now().toString(),
              rua: enderecoAntigo.rua || '',
              numero: enderecoAntigo.numero || '',
              adicional: enderecoAntigo.adicional || '',
              setor: setorAntigo,
              principal: true
            }]
          };
        }
        
        return {
          id: doc.id,
          ...clienteData
        };
      }) as Cliente[];
      
      setClientes(data);
      setIsLoading(false);
    }, (error) => {
      console.error('Erro ao carregar clientes:', error);
      alert('Falha ao carregar lista de clientes.');
      setIsLoading(false);
    });
  };

  const carregarSetores = async (tabelaTaxaId: string) => {
    try {
      const tabelaDoc = await getDoc(doc(db, 'tabelasTaxas', tabelaTaxaId));
      if (!tabelaDoc.exists()) {
        console.log('Tabela de taxas não encontrada');
        return;
      }

      const tabelaData = tabelaDoc.data() as TabelaTaxas;
      const setoresData = tabelaData.setores || [];
      
      if (setoresData.length === 0) {
        console.log('Nenhum setor encontrado na tabela de taxas');
        return;
      }

      setSetores(setoresData);
    } catch (error) {
      console.error('Erro ao carregar setores:', error);
      alert('Falha ao carregar setores. Por favor, tente novamente.');
    }
  };

  const migrarClientesAntigos = async (userId: string) => {
    try {
      const clientesSubcolecao = await getDocs(collection(db, 'usuarios', userId, 'clientes'));
      if (!clientesSubcolecao.empty) {
        console.log('Clientes já migrados para subcoleção do usuário');
        return;
      }

      const userDoc = await getDoc(doc(db, 'usuarios', userId));
      if (!userDoc.exists()) {
        console.log('Usuário não encontrado');
        return;
      }

      const userData = userDoc.data();
      const empresaId = userData.empresaId;

      if (!empresaId) {
        console.log('Usuário não tem empresaId');
        return;
      }

      const clientesAntigos = await getDocs(query(collection(db, 'clientes'), where('empresaId', '==', empresaId)));
      
      if (clientesAntigos.empty) {
        console.log('Nenhum cliente antigo encontrado para migrar');
        return;
      }

      console.log(`Migrando ${clientesAntigos.size} clientes para subcoleção do usuário...`);

      const batch = writeBatch(db);
      
      clientesAntigos.docs.forEach((docSnapshot) => {
        const clienteData = docSnapshot.data();
        const novoClienteRef = doc(collection(db, 'usuarios', userId, 'clientes'));
        
        const { empresaId: _, ...clienteSemEmpresaId } = clienteData;
        
        batch.set(novoClienteRef, {
          ...clienteSemEmpresaId,
          migradoEm: new Date()
        });
        
        batch.delete(docSnapshot.ref);
      });

      await batch.commit();
      console.log('Migração de clientes concluída com sucesso');
      
    } catch (error) {
      console.error('Erro ao migrar clientes antigos:', error);
    }
  };

  const handleEditCliente = (cliente: Cliente) => {
    setClienteEditando(cliente);
    setNovoCliente({
      nome: cliente.nome,
      telefone: cliente.telefone,
      enderecos: cliente.enderecos || []
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const handleAddEndereco = () => {
    setNovoCliente(prev => ({
      ...prev,
      enderecos: [
        ...prev.enderecos,
        criarEnderecoPadrao()
      ]
    }));
  };

  const handleRemoveEndereco = (enderecoId: string) => {
    if (novoCliente.enderecos.length <= 1) {
      alert('O cliente deve ter pelo menos um endereço.');
      return;
    }

    setNovoCliente(prev => ({
      ...prev,
      enderecos: prev.enderecos.filter(end => end.id !== enderecoId)
    }));
  };

  const handleSetEnderecoPrincipal = (enderecoId: string) => {
    setNovoCliente(prev => ({
      ...prev,
      enderecos: prev.enderecos.map(end => ({
        ...end,
        principal: end.id === enderecoId
      }))
    }));
  };

  const handleUpdateEndereco = (enderecoId: string, campo: keyof Endereco, valor: string | boolean) => {
    setNovoCliente(prev => ({
      ...prev,
      enderecos: prev.enderecos.map(end => 
        end.id === enderecoId ? { ...end, [campo]: valor } : end
      )
    }));
  };

  const handleSaveChanges = async () => {
    if (!userId) {
      alert('Usuário não identificado.');
      return;
    }

    if (!novoCliente.nome || !novoCliente.telefone) {
      alert('Preencha todos os campos obrigatórios.');
      return;
    }

    if (novoCliente.enderecos.some(end => !end.rua || !end.numero || !end.setor)) {
      alert('Preencha todos os campos de endereço (rua, número e setor).');
      return;
    }

    try {
      if (isEditing && clienteEditando) {
        await updateDoc(doc(db, 'usuarios', userId, 'clientes', clienteEditando.id), {
          ...novoCliente,
          atualizadoEm: serverTimestamp()
        });
        alert('Cliente atualizado com sucesso!');
      } else {
        await addDoc(collection(db, 'usuarios', userId, 'clientes'), {
          ...novoCliente,
          criadoEm: serverTimestamp()
        });
        alert('Cliente cadastrado com sucesso!');
      }

      setNovoCliente({
        nome: '',
        telefone: '',
        enderecos: [criarEnderecoPadrao()]
      });
      setShowModal(false);
      setIsEditing(false);
      setClienteEditando(null);
      setPesquisaSetor({});
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      alert('Não foi possível salvar o cliente.');
    }
  };

  const handleDeleteCliente = async (cliente: Cliente) => {
    if (!confirm(`Tem certeza que deseja excluir o cliente "${cliente.nome}"?`)) {
      return;
    }

    try {
      if (!userId) {
        alert('Usuário não identificado.');
        return;
      }
      await deleteDoc(doc(db, 'usuarios', userId, 'clientes', cliente.id));
      alert('Cliente excluído com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir cliente:', error);
      alert('Não foi possível excluir o cliente.');
    }
  };

  const filteredClientes = clientes.filter(cliente =>
    cliente.nome.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cliente.telefone.includes(searchQuery) ||
    cliente.enderecos.some(end => end.rua.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Função para filtrar setores por pesquisa
  const getSetoresFiltrados = (pesquisa: string) => {
    if (!pesquisa || pesquisa.trim() === '') {
      return setores;
    }
    return setores.filter(setor =>
      setor.nome.toLowerCase().includes(pesquisa.toLowerCase())
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200/50 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <button
              onClick={() => navigate('/empresa')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Lista de Clientes</h1>
              <p className="text-sm text-gray-500">Gerencie seus clientes cadastrados</p>
            </div>
            <button
              onClick={() => {
                setNovoCliente({
                  nome: '',
                  telefone: '',
                  enderecos: [criarEnderecoPadrao()]
                });
                setIsEditing(false);
                setClienteEditando(null);
                setShowModal(true);
              }}
              className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl"
            >
              + Novo Cliente
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Barra de Busca */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="relative">
            <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Buscar por nome, telefone ou endereço..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
            />
          </div>
        </div>

        {/* Lista de Clientes */}
        {filteredClientes.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {searchQuery ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado'}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchQuery 
                ? 'Tente ajustar sua busca' 
                : 'Adicione seu primeiro cliente para começar'
              }
            </p>
            {!searchQuery && (
              <button
                onClick={() => {
                  setNovoCliente({
                    nome: '',
                    telefone: '',
                    enderecos: [criarEnderecoPadrao()]
                  });
                  setIsEditing(false);
                  setClienteEditando(null);
                  setShowModal(true);
                }}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl"
              >
                Adicionar Cliente
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClientes.map((cliente) => {
              const enderecos = cliente.enderecos || [];
              const enderecosFormatados = enderecos.length === 0 && cliente.endereco 
                ? [{
                    id: Date.now().toString(),
                    rua: cliente.endereco.rua || '',
                    numero: cliente.endereco.numero || '',
                    adicional: cliente.endereco.adicional || '',
                    setor: cliente.setor || '',
                    principal: true
                  }]
                : enderecos;

              return (
                <div key={cliente.id} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all border border-gray-100">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                        {cliente.nome ? cliente.nome.charAt(0).toUpperCase() : 'C'}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{cliente.nome || 'Cliente sem nome'}</h3>
                        <p className="text-sm text-gray-600">{cliente.telefone || 'Sem telefone'}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditCliente(cliente)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteCliente(cliente)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {enderecosFormatados.map((endereco, index) => (
                      <div key={endereco.id || index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-2 mb-2">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-xs font-medium text-gray-700">
                            Endereço {index + 1}{endereco.principal && ' (Principal)'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 mb-2">
                          {endereco.rua || 'Sem rua'}, {endereco.numero || 'S/N'}
                          {endereco.adicional && ` - ${endereco.adicional}`}
                        </p>
                        <div className="flex gap-2 flex-wrap">
                          {endereco.principal && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-md">
                              Principal
                            </span>
                          )}
                          {endereco.setor && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-md">
                              {endereco.setor}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Modal de Adicionar/Editar Cliente */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-between">
              <h2 className="text-2xl font-bold">
                {isEditing ? 'Editar Cliente' : 'Novo Cliente'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setIsEditing(false);
                  setClienteEditando(null);
                  setPesquisaSetor({});
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome do Cliente *</label>
                  <input
                    type="text"
                    placeholder="Nome completo"
                    value={novoCliente.nome}
                    onChange={(e) => setNovoCliente(prev => ({ ...prev, nome: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Telefone *</label>
                  <input
                    type="tel"
                    placeholder="(00) 00000-0000"
                    value={novoCliente.telefone}
                    onChange={(e) => setNovoCliente(prev => ({ ...prev, telefone: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                  />
                </div>

                {novoCliente.enderecos.map((endereco, index) => (
                  <div key={endereco.id} className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Endereço {index + 1}</h3>
                      {novoCliente.enderecos.length > 1 && (
                        <button
                          onClick={() => handleRemoveEndereco(endereco.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Rua *</label>
                        <input
                          type="text"
                          placeholder="Rua"
                          value={endereco.rua}
                          onChange={(e) => handleUpdateEndereco(endereco.id, 'rua', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Número *</label>
                        <input
                          type="text"
                          placeholder="Nº"
                          value={endereco.numero}
                          onChange={(e) => handleUpdateEndereco(endereco.id, 'numero', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Adicional</label>
                        <input
                          type="text"
                          placeholder="Complemento, bloco, apto, etc."
                          value={endereco.adicional}
                          onChange={(e) => handleUpdateEndereco(endereco.id, 'adicional', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Setor *</label>
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Digite para buscar o setor..."
                            value={pesquisaSetor[endereco.id] || endereco.setor || ''}
                            onChange={(e) => {
                              setPesquisaSetor(prev => ({ ...prev, [endereco.id]: e.target.value }));
                              if (e.target.value === '') {
                                handleUpdateEndereco(endereco.id, 'setor', '');
                              }
                            }}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                          />
                          {pesquisaSetor[endereco.id] && pesquisaSetor[endereco.id].length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                              {getSetoresFiltrados(pesquisaSetor[endereco.id] || '')
                                .map((setor, idx) => (
                                  <button
                                    key={`${setor.nome}-${idx}`}
                                    onClick={() => {
                                      handleUpdateEndereco(endereco.id, 'setor', setor.nome);
                                      setPesquisaSetor(prev => ({ ...prev, [endereco.id]: '' }));
                                    }}
                                    className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0 ${
                                      endereco.setor === setor.nome ? 'bg-blue-50' : ''
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className={`font-semibold ${endereco.setor === setor.nome ? 'text-blue-700' : 'text-gray-900'}`}>
                                        {setor.nome}
                                      </span>
                                      <span className={`text-sm ${endereco.setor === setor.nome ? 'text-blue-600 font-bold' : 'text-blue-600 font-medium'}`}>
                                        R$ {setor.valor.toFixed(2).replace('.', ',')}
                                      </span>
                                    </div>
                                  </button>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={() => handleSetEnderecoPrincipal(endereco.id)}
                        className="flex items-center gap-3 p-3 bg-white rounded-lg border-2 border-gray-200 hover:border-blue-500 transition-colors"
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          endereco.principal 
                            ? 'bg-blue-600 border-blue-600' 
                            : 'border-gray-300'
                        }`}>
                          {endereco.principal && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="text-sm font-medium text-gray-700">Endereço Principal</span>
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  onClick={handleAddEndereco}
                  className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-all font-semibold"
                >
                  + Adicionar Outro Endereço
                </button>

                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setIsEditing(false);
                      setClienteEditando(null);
                      setPesquisaSetor({});
                    }}
                    className="flex-1 px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveChanges}
                    className="flex-1 px-6 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl"
                  >
                    {isEditing ? 'Atualizar Cliente' : 'Salvar Cliente'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListaClientes;

